/**
 * PII masking utilities for Datadog RUM
 * Masks sensitive data while preserving email addresses for user tracking
 */

import type { PIIMaskingRule, PIIMaskingOptions } from "./types";

/**
 * Default PII masking rules
 * NOTE: Email addresses are intentionally excluded to allow user tracking
 */
const DEFAULT_MASKING_RULES: PIIMaskingRule[] = [
  {
    name: "Credit Card",
    pattern: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
    replacement: "[CREDIT_CARD]",
  },
  {
    name: "SSN",
    pattern: /\b\d{3}-\d{2}-\d{4}\b/g,
    replacement: "[SSN]",
  },
  {
    name: "Phone Number",
    pattern: /(?:\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
    replacement: "[PHONE]",
  },
  {
    name: "IP Address",
    pattern: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
    replacement: "[IP_ADDRESS]",
  },
  {
    name: "Postal Code",
    pattern: /\b\d{5}(?:-\d{4})?\b/g,
    replacement: "[POSTAL_CODE]",
  },
];

/**
 * Apply masking rules to a string
 */
export function maskPIIInString(value: string, options: PIIMaskingOptions = {}): string {
  const rules = [...DEFAULT_MASKING_RULES, ...(options.customRules || [])];

  let maskedValue = value;
  for (const rule of rules) {
    maskedValue = maskedValue.replace(rule.pattern, rule.replacement);
  }

  return maskedValue;
}

/**
 * Recursively mask PII in an object
 */
export function maskPIIInObject(obj: unknown, options: PIIMaskingOptions = {}): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    return maskPIIInString(obj, options);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => maskPIIInObject(item, options));
  }

  if (typeof obj === "object") {
    const masked: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      masked[key] = maskPIIInObject(value, options);
    }
    return masked;
  }

  return obj;
}

/**
 * Convenience function for masking PII in any value
 */
export function maskPII(value: unknown, options?: PIIMaskingOptions): unknown {
  return maskPIIInObject(value, options);
}
